import './PardotEmailMetadataTab.scss';

import { convertISODateToPardotDate } from '@sonar-software/sonar-ts-lib';
import { LoadingSpinner } from '@ui/LoadingSpinner';
import { PardotEmailTemplate } from '@periscopes/general/email-templates';
import { SonarText } from '@ui/text';

import { COPY_SONAR_OBJECT_BASE, COPY_SPECIFIC } from '@sonar-software/sonar-ts-constants';
import { MetadataContainer, MetadataEntry } from '@periscopes/general/metadata';
import {
	PeriscopePardotEmailMetadata,
	PeriscopePardotEmailState,
	SonarObjectType
} from '@sonar-software/sonar-ts-types';
import React, { useCallback, useMemo } from 'react';
import { usePeriscopeActions, usePeriscopeMetadata } from '@hooks';

export interface PardotEmailMetadataTabProps {
	index: number;
	sonarObjectId: number;
}

function getStateAndDateMetadataEntries(metadata: PeriscopePardotEmailMetadata): Array<MetadataEntry> {
	switch (metadata.state) {
	case PeriscopePardotEmailState.Scheduled:
		return [
			{
				bold: true,
				label: COPY_SPECIFIC.STATE,
				type: 'string',
				value: metadata.state
			},
			{
				label: COPY_SPECIFIC.SCHEDULED_FOR,
				type: 'string',
				value: metadata.scheduledFor ? convertISODateToPardotDate(metadata.scheduledFor) : null
			}
		];
	case PeriscopePardotEmailState.Sent:
		return [
			{
				bold: true,
				label: COPY_SPECIFIC.STATE,
				type: 'string',
				value: metadata.state
			},
			{
				label: COPY_SPECIFIC.SENT_AT,
				type: 'string',
				value: metadata.scheduledFor ? convertISODateToPardotDate(metadata.scheduledFor) : null
			}
		];
	default:
		return [
			{
				bold: true,
				label: COPY_SPECIFIC.STATE,
				type: 'string',
				value: metadata.state
			}
		];
	}
}

export const PardotEmailMetadataTab: React.FC<PardotEmailMetadataTabProps> = ({ index, sonarObjectId }) => {
	const { openPeriscope } = usePeriscopeActions();

	const { loading, metadata } = usePeriscopeMetadata<PeriscopePardotEmailMetadata>(
		sonarObjectId,
		SonarObjectType.PardotEmail
	);

	const handleEmailTemplateClick = useCallback(() => {
		if (!metadata?.emailTemplate?.sonarObjectId) {
			return;
		}

		const { emailTemplate } = metadata;

		openPeriscope({
			id: emailTemplate.sonarObjectId,
			name: emailTemplate.displayName,
			openedFromIndex: index,
			type: emailTemplate.sonarObjectType ?? SonarObjectType.PardotEmailTemplate
		});
	}, [ index, openPeriscope, metadata ]);

	const metadataEntries = useMemo(() => {
		if (!metadata) {
			return [];
		}

		const stateAndDateMetadataEntries: MetadataEntry[] = getStateAndDateMetadataEntries(metadata);

		const entries: MetadataEntry[] = [
			...stateAndDateMetadataEntries,
			{
				label: COPY_SPECIFIC.SENDER,
				type: 'array',
				value: metadata.sender
			},
			{
				label: COPY_SPECIFIC.REPLY_TO,
				type: 'array',
				value: metadata.replyTo
			},
			{
				label: COPY_SPECIFIC.TYPE,
				type: 'string',
				value: metadata.type
			},
			{
				label: COPY_SPECIFIC.CONTENT_TYPE,
				type: 'string',
				value: metadata.contentType
			},
			{
				label: COPY_SPECIFIC.SUBJECT,
				type: 'string',
				value: metadata.subject
			},
			{
				label: COPY_SPECIFIC.BOUNCE,
				type: 'string',
				value: metadata.bounce
			}
		];

		return entries;
	}, [ metadata ]);

	if (loading) {
		return <LoadingSpinner spinning />;
	}

	return (
		<>
			<MetadataContainer
				entries={ metadataEntries }
				title={ COPY_SPECIFIC.EMAIL_INFORMATION } />
			{ metadata?.emailTemplate &&
				<>
					<SonarText className='email-template-title'>{ COPY_SONAR_OBJECT_BASE.EMAIL_TEMPLATE }</SonarText>
					<PardotEmailTemplate
						entity={ metadata.emailTemplate }
						onEmailTemplateClick={ handleEmailTemplateClick } />
				</> }
		</>
	);
};
